import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChildren
} from '@angular/core';
import { isNullOrWhiteSpace } from '@classifieds-sell/shared';
import { FormHelperService } from '../../../helpers/form-helper.service';
import { DraftFacade } from '@classifieds-sell/state';
import { Subject } from 'rxjs';
import {
    PropertyAgentContactChanges,
    PropertyAgentContactDetailsType
} from '../../../models/property-agent-contact-details/property-agent-contact-details-model';
import { PropertySectionBase } from '../../property-section-base';
import { NgForm } from '@angular/forms';
import { PropertyAgentContactDetailComponent } from './property-agent-contact-detail/property-agent-contact-detail.component';
import { Photo } from '@classifieds-sell/trademe-core-ui';
import { v4 as uuidv4 } from 'uuid';

@Component({
    selector: 'property-agent-contact-details-section',
    templateUrl: './property-agent-contact-details-section.component.html',
    styleUrls: ['./property-agent-contact-details-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PropertyAgentContactDetailsSectionComponent
    extends PropertySectionBase
    implements OnInit, OnDestroy {
    private _destroyed$: Subject<void> = new Subject<void>();

    initiated = false;

    hasErrorInContactDetails = false;
    hasErrorInSecondContactDetails = false;

    /**
     * for UI change
     * + form validation not to work for second details section when false
     */
    isSecondContactDetailAdded = false;

    /**
     * for notifying property-home.component that there has been a change
     * whenever the second details section was removed
     * (property-home.component save draft only when there is a change from existing state)
     *
     * (editing field values & adding second details section are being notified as changes,
     * through NgForm.dirty api)
     */
    isSecondContactDetailRemoved = false;

    phoneValidations: Record<PropertyAgentContactDetailsType, {landLine: any; mobile: any}> = {
        [PropertyAgentContactDetailsType.primary]: {
            landLine: null,
            mobile: null
        },
        [PropertyAgentContactDetailsType.secondary]: {
            landLine: null,
            mobile: null
        }
    };

    firstAgentContactDetailForm: NgForm;
    secondAgentContactDetailForm: NgForm;

    @ViewChildren(PropertyAgentContactDetailComponent)
    set agentContactDetailForms(form: QueryList<PropertyAgentContactDetailComponent>) {
        this.firstAgentContactDetailForm = form.first.agentContactDetailForm;
        this.secondAgentContactDetailForm = form.last.agentContactDetailForm;
    }

    constructor(
        public draftFacade: DraftFacade,
        private formHelperService: FormHelperService,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        super(draftFacade);
    }

    ngOnDestroy(): void {
        super.onDestroy();
        this._destroyed$.next();
    }

    ngOnInit(): void {
        super.onInit(() => {
            this.setPhoneValidations();

            if (!this.initiated) {
                const {
                    secondName
                } = this.draftData.propertyInformation;

                if (secondName) {
                    this.isSecondContactDetailAdded = true;
                }

                this.initiated = true;
            }

            this.changeDetectorRef.markForCheck();
        });
    }

    get propertyAgentContactDetailsType(): typeof PropertyAgentContactDetailsType {
        return PropertyAgentContactDetailsType;
    }

    isValid(): boolean {
        this.hasErrorInContactDetails = false;
        this.hasErrorInSecondContactDetails = false;

        this.formHelperService.validate(this.firstAgentContactDetailForm);
        if (this.isSecondContactDetailAdded) {
            this.formHelperService.validate(this.secondAgentContactDetailForm);
        }

        let validationResult = this.validateForm();
        this.changeDetectorRef.markForCheck();

        validationResult = this.firstAgentContactDetailForm.form.valid && validationResult;
        if (this.isSecondContactDetailAdded && validationResult) {
            validationResult = this.secondAgentContactDetailForm.form.valid;
        }

        return validationResult;
    }

    dirty(): boolean {
        let isDirty = this.firstAgentContactDetailForm.form.dirty || this.isSecondContactDetailRemoved;
        if (this.isSecondContactDetailAdded && !isDirty) {
            isDirty = this.secondAgentContactDetailForm.form.dirty;
        }
        return isDirty;
    }

    onContactDetailsChange(contactChanges: PropertyAgentContactChanges) {
        if (contactChanges.type === PropertyAgentContactDetailsType.primary) {
            this.hasErrorInContactDetails = false;
        } else {
            this.hasErrorInSecondContactDetails = false;
        }

        if (contactChanges.agentPhoto) {
            if (contactChanges.type === PropertyAgentContactDetailsType.primary) {
                this.firstAgentContactDetailForm.form.markAsDirty();
            } else {
                this.secondAgentContactDetailForm.form.markAsDirty();
            }

            const draftDataKey = 'propertyInformation';
            const photoId = uuidv4();
            const newPhoto = { photoId, loading: true, hasError: false} as Photo;

            if (!contactChanges.isRemoved) {
                this.draftFacade.addAgentPhotoToDraftData(
                    newPhoto, contactChanges.agentPhoto, contactChanges.type, draftDataKey);
            } else {
                this.draftFacade.removeAgentPhotoFromDraftData(
                    contactChanges.type,
                    draftDataKey
                );
            }
        }

        this.changeDetectorRef.markForCheck();

        this.publish();
    }

    onAddSecondContact() {
        this.isSecondContactDetailAdded = true;
        this.changeDetectorRef.markForCheck();
    }

    onRemoveSecondContact() {
        this.isSecondContactDetailRemoved = true;
        this.isSecondContactDetailAdded = false;
        this.removeSecondDetails();
        this.hasErrorInSecondContactDetails = false;
        this.changeDetectorRef.markForCheck();
        this.publish();
    }

    private setPhoneValidations = () => {
        const {
            mobile,
            landLine,
            secondMobile,
            secondLandLine
        } = this.validation.propertyInformation;

        this.phoneValidations = {
            [PropertyAgentContactDetailsType.primary]: {
                landLine: { ...landLine, isAllowAllTypes: true },
                mobile: { ...mobile, isAllowAllTypes: true }
            },
            [PropertyAgentContactDetailsType.secondary]: {
                landLine: { ...secondLandLine, isAllowAllTypes: true },
                mobile: { ...secondMobile, isAllowAllTypes: true }
            }
        };
    };

    private removeSecondDetails = () => {
        this.draftFacade.updateDraftData({
            ...this.draftData,
            propertyInformation: {
                ...this.draftData.propertyInformation,
                secondName: null,
                secondMobile: null,
                secondLandLine: null,
                secondEmail: null,
                secondBrandingImageId: 0,
                secondPhotoUrl: null
            }
        });
    };

    private validateForm = (): boolean => {
        if (
            isNullOrWhiteSpace(
                this.draftData.propertyInformation.mobile
            ) &&
            isNullOrWhiteSpace(
                this.draftData.propertyInformation.landLine
            ) &&
            isNullOrWhiteSpace(this.draftData.propertyInformation.email)
        ) {
            this.hasErrorInContactDetails = true;
        }

        if (!this.isSecondContactDetailAdded) {
            return !this.hasErrorInContactDetails;
        }

        if (
            isNullOrWhiteSpace(
                this.draftData.propertyInformation.secondMobile
            ) &&
            isNullOrWhiteSpace(
                this.draftData.propertyInformation.secondLandLine
            ) &&
            isNullOrWhiteSpace(this.draftData.propertyInformation.secondEmail)
        ) {
            this.hasErrorInSecondContactDetails = true;
            return false;
        }

        return true;
    };
}
